import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { IUserData_ShakerControlProfile } from 'src/app/model/shaker-control/shaker-control-userdata-template-types';
import { ShakerProfileFeatureService } from 'src/app/services/shaker-profile/shaker-profile-feature.service';

import { Observable, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { UserDataService } from 'src/app/services/user-data/user-data.service';


/**
 * Structure used to provide data required to build the list of available
 * `<mat-option>`-list to the template.
 */
type ITemplateOption = {
  _id: string;
  displayName: string;
};


export type ProfileOrCustomIndicator =
  IUserData_ShakerControlProfile
  | ShakerControlProfileSelectionComponent['CustomProfileIndicator'];


@Component({
  selector: 'app-shaker-control-profile-selection',
  imports: [
    MaterialImportsModule,
    CommonModule,
  ],
  templateUrl: './shaker-control-profile-selection.component.html',
  styleUrls: ['./shaker-control-profile-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShakerControlProfileSelectionComponent {

  /**
   * Special ID denoting a custom/'local' profile, which does exist in the database.
   */
  protected readonly CustomProfileIndicator = UserDataService.customTemplateIndicator;


  /**
   * The selected profile.
   *
   * Either the ID of a template or the {@link IUserData} object holding the template.
   */
  @Input()
  public set selectedProfile(value: Required<IUserData_ShakerControlProfile>['_id'] | IUserData_ShakerControlProfile) {
    if (typeof value === 'string') {
      this.selectedProfileIdStore = value;
    } else {
      if (value._id === undefined) {
        throw new Error('Invalid profile object provided. Missing \'_id\' property.');
      }
      this.selectedProfileIdStore = value._id;
    }
  }


  @Input()
  public disabled = false;


  /**
   * Emits the selected profile or the 'custom profile indicator'.
   */
  @Output()
  public selectedProfileChange = new EventEmitter<ProfileOrCustomIndicator>();


  private shakerFeatureService = inject(ShakerProfileFeatureService);


  /**
   * @private
   */
  public get _selectedProfileId(): string | null {
    return this.selectedProfileIdStore;
  }


  /**
   * @private
   */
  public set _selectedProfileId(newId: string) {
    if (newId === this.selectedProfileIdStore) {
      return;
    }

    this.selectedProfileIdStore = newId;

    if (newId === this.CustomProfileIndicator) {
      this.selectedProfileChange.emit(this.CustomProfileIndicator);
      return;
    }

    void firstValueFrom(this.profileCache$)
      .then(profileList => {
        const profile = profileList.find(profile => profile._id === newId);
        if (!profile) {
          throw new Error(`Profile with ID '${newId}' not found.`);
        }

        this.selectedProfileChange.emit(profile);
      });
  }


  /**
   * Backing store for {@link _selectedProfileId}
   */
  private selectedProfileIdStore: string | null = null;


  /**
   * @private
   */
  protected availableOptions$: Observable<ITemplateOption[]> = this.shakerFeatureService.getShakerControlProfiles()
    .pipe(
      tap(profiles => this.profileCache$.next(profiles)),
      map(
        userDataList => userDataList.map(
          userData => ({
            _id: userData._id!,
            displayName: userData.value.template.name
          })
        )
      )
    );


  private profileCache$ = new ReplaySubject<IUserData_ShakerControlProfile[]>(1);
}
